import { useEffect, useRef } from 'react';

import type { DecisionFormData } from './useDecisionFormData';

interface UseLabelSmartInferenceProps {
  formData: DecisionFormData;
  setFormData: (updater: (prev: DecisionFormData) => DecisionFormData) => void;
}

/**
 * Unidirectional smart inference for label assessments.
 *
 * Direction: correctedLabels changes → may set labelsCorrect=false (one-way only)
 *
 * - When corrected labels are added, automatically sets labelsCorrect to false
 *   (disagreement is inferred from corrections).
 * - Changes to labelsCorrect alone (e.g. manual toggle via like/dislike buttons)
 *   do NOT re-trigger this effect — this is intentional, not a gap.
 * - "Mark All Correct" clears correctedLabels to [], which fires the effect but
 *   the hasCorrectedLabels check prevents any state change.
 *
 * Why unidirectional? Bidirectional enforcement (where labelsCorrect=true would
 * re-clear itself when correctedLabels is non-empty) caused an infinite update
 * loop with "Mark All Correct" (AB#61317671). The current design lets auditors
 * manually override via like/dislike buttons without the effect fighting them.
 */
export function useLabelSmartInference({ formData, setFormData }: UseLabelSmartInferenceProps) {
  // Use ref to track previous corrected labels state and prevent infinite loops
  // Initialize with current state so the effect doesn't fire on initial render
  // (prevents flipping labelsCorrect to false when loading a "Like" decision
  // where correctedLabels defaults to originalLabels)
  const prevCorrectedLabelsRef = useRef<string>(formData.correctedLabels.join(','));
  // Use ref to read labelsCorrect without subscribing to changes — prevents feedback loop
  // where "Mark All Correct" setting labelsCorrect=true re-triggers this effect (AB#61317671)
  const labelsCorrectRef = useRef(formData.labelsCorrect);

  labelsCorrectRef.current = formData.labelsCorrect;

  // Smart inference (unidirectional): only fires when correctedLabels changes,
  // NOT when labelsCorrect changes. labelsCorrect is read via ref, not deps.
  useEffect(() => {
    const correctedLabelsState = formData.correctedLabels.join(',');

    // Check if state has changed to prevent infinite loops
    if (correctedLabelsState === prevCorrectedLabelsRef.current) {
      return;
    }

    // Update ref first
    prevCorrectedLabelsRef.current = correctedLabelsState;

    // If corrected labels are provided, set labelsCorrect to false
    // This indicates disagreement with original labels since corrections are being made
    const hasCorrectedLabels = formData.correctedLabels.length > 0;

    if (hasCorrectedLabels && labelsCorrectRef.current !== false) {
      setFormData(prev => ({ ...prev, labelsCorrect: false }));
    }
  }, [formData.correctedLabels, setFormData]);

  // Reverse direction: when labelsCorrect is set to true (agree), clear corrected labels
  // This prevents the contradictory state of "agree with labels" + "corrections provided"
  const prevLabelsCorrectRef = useRef(formData.labelsCorrect);

  useEffect(() => {
    const prevValue = prevLabelsCorrectRef.current;

    prevLabelsCorrectRef.current = formData.labelsCorrect;

    // Only act when labelsCorrect transitions TO true (not on initial render or other changes)
    if (formData.labelsCorrect === true && prevValue !== true && formData.correctedLabels.length > 0) {
      // Clear corrected labels and update the ref to prevent the other effect from re-triggering
      prevCorrectedLabelsRef.current = '';
      setFormData(prev => ({ ...prev, correctedLabels: [] }));
    }
  }, [formData.labelsCorrect, formData.correctedLabels.length, setFormData]);
}
